
import { Options, Vue } from "vue-class-component";
import CreateBookingClient from "../../../booking/create-booking-v3/create-booking-client.vue";
import {
  BookingV3,
  CommodityCIPL,
  DocInternationalModelValueType
} from "@/domain/entities/CreateBookingV3";
import { ShipmentBookingController } from "@/app/ui/controllers/ShipmentBookingController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import {
  formatInputMoney,
  formatInputMoneyWithDecimal
} from "@/app/infrastructures/misc/Utils";
import { CommodityController } from "@/app/ui/controllers/CommodityController";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { RequestListCommodity } from "@/data/payload/api/CommodityApiRequest";
import { DistrictData } from "@/domain/entities/Location";
import { OptionsClass } from "@/domain/entities/MainApp";
import { ShipmentBookingDetail } from "@/domain/entities/ShipmentBooking";
import FilterClient from "../../../booking/create-booking-v3/components/filter-client/index.vue";
import { ClientManagementController } from "@/app/ui/controllers/ClientManagementController";
import { DetailClientManagement } from "@/domain/entities/ClientManagement";
import DetailReceiver from "../../../booking/create-booking-v3/components/detail-receiver/index.vue";
import { Commodity } from "@/domain/entities/Commodity";
import { FlagsPermissionBookingV3 } from "@/feature-flags/flag-booking-v3";

@Options({
  components: {
    CreateBookingClient
  }
})
export default class DuplicateClient extends Vue {
  booking: CreateBookingClient | any = null;
  form = new BookingV3();
  productName = "";
  firstGetUpselling = true;

  get isError() {
    return ShipmentBookingController.isError;
  }
  get errorDetail() {
    return ShipmentBookingController.isErrorCause;
  }

  async getDetail() {
    const res = await ShipmentBookingController.getEditBookingDetail({
      id: this.$route.query.id as any,
      sttNo: this.$route.params.id as any,
      isAuth: true,
      pageType: "duplicate_stt"
    });

    this.booking = (this.$refs as any).booking as CreateBookingClient;
    this.form = this.booking.form;
    const response: ShipmentBookingDetail = res as ShipmentBookingDetail;
    this.productName = response.sttProductTypeName;

    if (AccountController.accountData.account_type === "internal") {
      const origin = (await LocationController.getDetailDistrictByDistrictCode({
        districtCode: response.sttOriginDistrictId
      })) as DistrictData;
      LocationController.setDetailDistrict(origin);
    }

    await this.mapClient(response);
    const resp = (await ClientManagementController.getDetailV2(
      response.sttClient.clientId
    )) as DetailClientManagement;
    this.form.clientName.isFtz = resp.isFtz === "yes";
    // await this.mapSttManualReference(response); // dont populate stt manual and reference external; 
    this.mapSenderForm(response);
    await this.mapRecipientForm(response);
    this.mapDeliveryService(response);
    await this.mapCommodityForm(response);
    await this.mapPackageDetail(response);
    this.mapDocInternationalAttachmentForm(response);
    await this.mapDocInternationalForm(response);
    this.mapFtzAttachmentForm(response);
    await this.mapFtzForm(response);

    this.form.woodpacking = response.sttIsWoodPacking;

    if (response.sttInsuranceName.toLowerCase().includes("free"))
      this.form.insurance = "free";

    await this.$nextTick();

    this.mapProductName(response);

    this.booking.goBack = () => {
      this.booking.$router.push("/booking-v2");
    };
  }

  async created() {
    this.getDetail();
  }

  get accountIsNonForeign() {
    return !AccountController.accountData.accountIsForeign;
  }

  async mapClient(response: ShipmentBookingDetail) {
    if (!response.sttClient.clientId) return;
    const filterClient = (this.booking.$refs as any)[
      "filter-client"
    ] as FilterClient;
    if (!filterClient) return;

    await filterClient.fetchClients({
      search: response.sttClient.clientName,
      isFirstFetch: true
    });

    await this.$nextTick();

    if (filterClient.clientOptions.length) {
      filterClient.clientValue = filterClient.clientOptions[0];
      filterClient.onUpdateClient(filterClient.clientOptions[0]);
    }
  }

  mapSenderForm(response: ShipmentBookingDetail) {
    // sender
    this.form.sender = response.sttSenderName;
    this.form.phoneCodeSender = response.sttSenderPhoneCode;
    this.form.phoneSender = response.sttSenderPhone;
    this.form.addressSender = response.sttSenderAddress;
  }

  async mapRecipientForm(response: ShipmentBookingDetail) {
    // receiver
    this.form.receiver = response.sttRecipientName;
    this.form.phoneCodeReceiver = response.sttRecipientPhoneCode;
    this.form.phoneReceiver = response.sttRecipientPhone;
    this.form.addressReceiver = response.sttRecipientAddress;
    this.form.addressReceiverType = new OptionsClass({
      value: response.sttRecipientAddressType
    });

    let destinationDistrict = await LocationController.getDistrictList({
      limit: 40,
      page: 1,
      search: response.sttDestinationDistrictName,
      status: "active",
      cache: true,
      excludeCountry: ["INTERPACK", "INTERPACKMAXI"].includes(response.sttProductTypeName) ? "ID" : ""
    });

    if (!destinationDistrict.length && response.sttDestinationDistrictId) {
      const dist = await LocationController.getDetailDistrictByDistrictCode({
        districtCode: response.sttDestinationDistrictId
      });
      if (dist && dist.id) {
        destinationDistrict = [dist];
      }
    }

    LocationController.districtData.districtData = destinationDistrict
      .filter((key: any) => key.status.toLowerCase() === "active")
      .map(
        (e: any) =>
          new DistrictData({
            ...e,
            name: `${e.name}, ${e.cityName}`
          })
      );

    await this.$nextTick();

    ((this.booking.$refs as any)[
      "detail-receiver"
    ] as DetailReceiver).dataListDistrict =
      LocationController.districtData.districtData;

    this.form.destinationCity = (this.booking.$refs as any)[
      "detail-receiver"
    ].destinationCityList[0];
    await this.$nextTick();
    await (this.$refs as any).booking.$refs[
      "detail-receiver"
    ].onSelectDestinationCity();
  }

  mapDeliveryService(response: ShipmentBookingDetail) {
    // delivery service
    this.form.sttIsCod = response.sttIsCod;
    this.form.sttIsDfod = response.sttIsDfod;
  }

  async mapCommodityForm(response: ShipmentBookingDetail) {
    await this.$nextTick();
    // commodity
    const commodities = await CommodityController.getCommodityList(
      new RequestListCommodity({
        search: response.sttCommodityName,
        page: 1,
        limit: 20,
        commodityStatus: "active"
      })
    );

    const commodityDetail = (await CommodityController.getCommodityDetail({
      id: response.sttCommodityId
    })) as Commodity;

    const commodityValue = commodities.data.filter(
      (data) => data.commodity_id === response.sttCommodityId
    );

    (this.booking.$refs as any).commodity.dataListCommodities = commodityValue;

    this.form.commodity = {
      id: commodityDetail.commodity_id,
      name: this.accountIsNonForeign
        ? commodityDetail.commodity_name
        : commodityDetail.commodity_name_en,
      code: commodityDetail.commodity_code,
      status: commodityDetail.commodity_status,
      isDangerousGoods: commodityDetail.isDangerousGoods,
      isQuarantine: commodityDetail.isQuarantine
    } as any;
  }

  async mapPackageDetail(response: ShipmentBookingDetail): Promise<void> {
    await this.$nextTick();
    // package detail
    this.form.estimationPrice = response.sttGoodsEstimatePrice;
    this.form.codAmount = response.sttCodAmount.toString();

    // pieces
    this.form.pieces = response.SttPieces.map((piece) => ({
      ...piece,
      grossWeight: piece.grossWeight.toString(),
      volumeWeight: piece.volumeWeight.toString(),
      errorHws: false,
      errorGrossWeight: "",
      errorVolumeWeight: ""
    })) as any;
  }

  async mapProductName(response: ShipmentBookingDetail) {
    this.form.productName = response.sttProductTypeName;
    this.productName = response.sttProductTypeName;
  }

  mapDocInternationalAttachmentForm(response: ShipmentBookingDetail) {
    if (!response.sttAttachFiles.length) {
      response.sttAttachFiles = ["", ""];
    }

    if (response.sttAttachFiles.length == 1) response.sttAttachFiles.push("");
  }

  mapFtzAttachmentForm(response: ShipmentBookingDetail) {
    if (!response.sttFtzAttachFiles.length) {
      response.sttFtzAttachFiles = ["", ""];
    }

    if (response.sttFtzAttachFiles.length === 1)
      response.sttFtzAttachFiles.push("");
  }

  async mapDocInternationalForm(
    response: ShipmentBookingDetail
  ): Promise<void> {
    await this.$nextTick();

    if (!response.sttCipl.length) {
      this.form.docInternationalForm = new DocInternationalModelValueType();
    } else {
      this.form.docInternationalForm.listCommodityCIPL = response.sttCipl.map(
        (cipl: any) => {
          return new CommodityCIPL({
            id: new Date().getTime() + Math.random(),
            commodity: {
              name: this.accountIsNonForeign
                ? cipl.item_detail
                : cipl.item_detail_en,
              nameId: cipl.item_detail,
              nameEn: cipl.item_detail_en,
              code: cipl.item_detail
            } as any,
            goodsPrice: this.formatCIPLGoodsPrice(cipl.item_price),
            name: cipl.item_detail,
            quantity: cipl.quantity.toString()
          });
        }
      );
    }
      this.form.docInternationalForm.dutyTaxTerms =
        response.sttDutyTaxTerms || "DAP";
      this.form.docInternationalForm.email = response.sttRecipientEmail;
      this.form.docInternationalForm.idNumber = response.sttIdentityNumber;
      this.form.docInternationalForm.taxNumber = response.sttInterTaxNumber;
      this.form.docInternationalForm.imageIdUrl = response.sttKtpImage;
      this.form.docInternationalForm.imageTaxUrl = response.sttTaxImage;
      this.form.docInternationalForm.imageBeforePackingUrl =
        response.sttAttachFiles[0];
      this.form.docInternationalForm.imageAfterPackingUrl =
        response.sttAttachFiles[1];

      await this.$nextTick();

      (this.booking.$refs as any)["stt-manual-reference"].validateRefNumber(
        this.form.referenceExternal
      );

      (this.booking.$refs as any)["stt-manual-reference"].validateSttManual(
        this.form.sttManual,
        this.form
      );
      (this
        .booking as CreateBookingClient).errorDocInternational.listCommodityCIPLDocInternational = [];
      this.form.docInternationalForm.listCommodityCIPL.forEach(() => {
        (this
          .booking as CreateBookingClient).errorDocInternational.listCommodityCIPLDocInternational.push(
          ["", "", ""]
        );
      });
  }

  async mapFtzForm(response: ShipmentBookingDetail): Promise<void> {
    await this.$nextTick();
    if (!response.sttFtzCipl.length) {
      this.form.ftzForm = new DocInternationalModelValueType();
    } else {
      this.form.ftzForm.listCommodityCIPL = response.sttFtzCipl.map(
        (cipl: any) => {
          return new CommodityCIPL({
            id: new Date().getTime() + Math.random(),
            commodity: {
              name: this.accountIsNonForeign
                ? cipl.item_detail
                : cipl.item_detail_en,
              nameId: cipl.item_detail,
              nameEn: cipl.item_detail_en,
              code: cipl.item_detail
            } as any,
            goodsPrice: this.formatCIPLGoodsPrice(cipl.item_price),
            name: cipl.item_detail,
            quantity: cipl.quantity.toString()
          });
        }
      );
    }
      this.form.ftzForm.email = response.sttFtzRecipientEmail;
      this.form.ftzForm.idNumber = response.sttFtzIdentityNumber;
      this.form.ftzForm.taxNumber = response.sttTaxNumber;
      this.form.ftzForm.imageIdUrl = response.sttFtzKtpImage;
      this.form.ftzForm.imageTaxUrl = response.sttFtzTaxImage;
      this.form.ftzForm.imageBeforePackingUrl = response.sttFtzAttachFiles[0];
      this.form.ftzForm.imageAfterPackingUrl = response.sttFtzAttachFiles[1];

      if (!response.sttSenderStatus || !this.booking.isFtzAccount) return;

      if (this.accountIsNonForeign) {
        this.form.ftzForm.shipmentStatus = (this.booking
          .$refs as any).ftz.shipmentStatusOptions.find(
          (status: OptionsClass) => status.value === response.sttSenderStatus
        );
      } else {
        this.form.ftzForm.shipmentStatus = (this.booking
          .$refs as any).ftz.shipmentStatusInterpackOptions.find(
          (status: OptionsClass) => status.value === response.sttSenderStatus
        );
      }
      (this
        .booking as CreateBookingClient).errorFtz.listCommodityCIPLDocInternational = [];
      this.form.ftzForm.listCommodityCIPL.forEach(() => {
        (this
          .booking as CreateBookingClient).errorFtz.listCommodityCIPLDocInternational.push(
          ["", "", ""]
        );
      });
  }

  async mapSttManualReference(response: ShipmentBookingDetail): Promise<void> {
    await this.$nextTick();
    const sttNo = response.sttNo;
    this.form.referenceExternal = response.sttNoRefExternal;
    if (
      sttNo.match(/^10LP/gi) ||
      sttNo.match(/^98LP/gi) ||
      sttNo.match(/^19LP/gi)
    ) {
      this.form.sttManual = response.sttNo;
      (this.booking.$refs as any)["stt-manual-reference"].validateSttManual(
        response.sttNo,
        this.form
      );
    }
    (this.booking.$refs as any)["stt-manual-reference"].validateRefNumber(
      response.sttNoRefExternal
    );
  }

  setProductName() {
    const availableProduct: string[] = FlagsPermissionBookingV3.config_upselling_product_list.getValue().client;
    const booking = this.booking as CreateBookingClient;
    const isProductAvailable = booking.upsellingData.find(
      (data) =>
        data.productType.toLowerCase() === this.productName.toLowerCase() && availableProduct.includes(this.productName.toUpperCase())
    );
    if (this.firstGetUpselling && isProductAvailable) {
      this.form.productName = this.productName;
      this.firstGetUpselling = false;
    }
  }

  formatCIPLGoodsPrice(value = 0) {
    return this.accountIsNonForeign
      ? formatInputMoney(value.toString().replace(/[\D]+/g, ""))
      : formatInputMoneyWithDecimal(value);
  }
}
